import {chain} from 'lodash';
import React, {Component, PropsWithChildren} from 'react';
import {connect} from 'react-redux';
import {Dispatch} from 'redux';
import {Observable, Subscription} from 'rxjs';
import {distinctUntilChanged} from 'rxjs/operators';

import {inAnimationFrameOrHiddenCallbackAsync} from '../../../libs/shared/core/src/helpers/async/inAnimationFrame';
import {TaskSourcesEnum} from '../../../libs/shared/core/src/helpers/instrumentation/eventLoopPerformanceHelpers';
import {CancelToken} from '../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {isVisitorTeammate} from '../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {reconnectRuntimeFeatureFlagsAsync} from '../../../libs/shared/core/src/services/featureFlag/featureFlags';
import {postAsync} from '../../../libs/shared/core/src/services/http/http';
import {networkMonitor} from '../../../libs/shared/core/src/services/network/networkMonitor';
import {getHasPushV2} from '../../../libs/shared/core/src/services/push/common/pushSelectors';
import {PushContext} from '../../../libs/shared/core/src/services/push/pushContext';
import {buildOfflineStateObservable} from '../../../libs/shared/core/src/services/push/pushOfflineHelpers';
import {getPushConfig} from '../../../libs/shared/core/src/store/data/config/configSelectors';
import {syncStashWithServerAsyncAction} from '../../../libs/shared/core/src/store/data/stash/stashThunkActions';
import {getHasOfflineBannerAdjustment} from '../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {ResolveDispatchProps} from '../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../srcCommon/helpers/reselectHelpers';
import {makeDesktopPushEventSubscriptions} from '../../../srcCommon/push/desktopPushEventSubscriptions';
import {buildDesktopPushReconnectionObservable$} from '../../../srcCommon/push/pushCommons';
import {
  makeEventSubscriptionsDelegate,
  PushCoordinator,
  PushCoordinatorConfiguration,
} from '../../../srcCommon/push/pushCoordinator';
import {PushEventSubscriptionsDelegate} from '../../../srcCommon/push/pushEventSubscriptions';
import {makePresenceSubscriptions} from '../../../srcCommon/push/pushPresenceSubscriptions';
import {PushBaseProps} from '../../../srcCommon/push/pushTypes';
import {ImpersonationRequestModel} from '../../models/api/impersonationRequestModel';
import {getCompany, getIsNotificationCenterEnabled} from '../../store/data/company/companySelectors';
import {pushConversationUpdatedAction} from '../../store/data/conversations/pushConversationsActions';
import {refreshBootAction} from '../../store/data/dataActions';
import {getUser} from '../../store/data/teammates/teammatesSelectors';
import {
  SyncCellsListsReasonsEnum,
  trySyncUserCellsListsAsyncAction,
} from '../../store/metadata/cellsLists/cellsListsActions';
import {getAnalyticsPanelsRooms} from '../../store/panels/analytics/analyticsPanelSelectors';
import {getCellsPanelsRooms} from '../../store/panels/cells/cellsPanelSelectors';
import {getComposerPanelsRooms} from '../../store/panels/composer/composerPanelSelectors';
import {
  getViewerPanelsConversationIds,
  getViewerPanelsRooms,
} from '../../store/panels/viewer/viewerPanelSelectors';
import {pushStatusDisconnectedAction} from '../../store/pushStatus/pushStatusActions';
import {ImpersonationRequestBanner} from '../app/banners/impersonation/impersonationRequestBanner';
import {OfflineBanner} from '../app/banners/offline/offlineBanner';
import {FailedDraftBanner} from './failedDraftBanner';

/*
 * Props.
 */

interface PushStateProps extends PushBaseProps {
  viewerPanelsRooms: ReadonlyArray<string>;
  composerPanelsRooms: ReadonlyArray<string>;
  analyticsPanelsRooms: ReadonlyArray<string>;
}

export interface PushProps extends PushStateProps, ResolveDispatchProps<typeof mapDispatchToProps> {
  dispatch: Dispatch;
  onRawBoot?: (rawBoot: unknown) => void;
  isPushV2Enabled: boolean;
  hasOfflineBannerAdjustment: boolean;
}

/*
 * Component.
 */

interface PushState {
  /** Offline UI state. */
  isOffline: boolean;
  connectionKey: number;
  impersonationRequest?: ImpersonationRequestModel;
  failedDraftDescription?: string;
}

/* eslint react/no-unused-state: 0 */
class Push extends Component<PropsWithChildren<PushProps>, PushState> {
  constructor(props: PropsWithChildren<PushProps>) {
    super(props);

    this.isOnlineSubscription = new Subscription();

    this.state = {
      isOffline: !navigator.onLine,
      connectionKey: 1,
    };

    const coordinatorConfig: PushCoordinatorConfiguration = {
      pushConfiguration: props.pusherConfiguration,
      company: props.company,
      user: props.user,
    };

    this.pushCoordinator = new PushCoordinator(
      coordinatorConfig,
      props.pushConversationUpdated,
      this.refreshContent.bind(this),
    );
  }

  private readonly pushCoordinator: PushCoordinator;

  private isOnlineSubscription: Subscription;

  private subscribeToOfflineStatus(isOnlineChanges$: Observable<boolean>) {
    const offlineState$ = buildOfflineStateObservable(
      isOnlineChanges$,
      this.pushCoordinator.pushDisconnectEvents$,
      this.props.hasOfflineBannerAdjustment,
    );
    this.isOnlineSubscription.add(offlineState$.subscribe((isOffline) => this.setState({isOffline})));
  }

  private subscribeToPushDisconnectEvents() {
    this.isOnlineSubscription.add(
      this.pushCoordinator.pushDisconnectEvents$.pipe(distinctUntilChanged()).subscribe((isDisconnected) => {
        if (isDisconnected) {
          this.props.pushStatusDisconnected();
        }
      }),
    );
  }

  /*
   * Lifecycle.
   */

  componentDidMount() {
    const isOnlineChanges$ = networkMonitor.isOnline$.pipe(distinctUntilChanged());
    const pushLifecycle$ = buildDesktopPushReconnectionObservable$();
    this.subscribeToOfflineStatus(isOnlineChanges$);
    this.subscribeToPushDisconnectEvents();

    // Register lifecycle events.
    const rooms = this.getRooms();
    const presences = this.props.isPushV2Enabled
      ? []
      : makePresenceSubscriptions(this.props.dispatch, this.pushCoordinator);
    const events = this.getEventSubscriptions();
    this.pushCoordinator.setup(
      rooms,
      presences,
      events,
      this.relayedEvents,
      isOnlineChanges$,
      pushLifecycle$,
    );
  }

  componentDidUpdate() {
    const rooms = this.getRooms();
    this.pushCoordinator.updateRoomSubscriptions(rooms);
  }

  componentWillUnmount() {
    this.isOnlineSubscription.unsubscribe();

    this.pushCoordinator.destroy();
  }

  /*
   * Push events.
   */

  private getEventSubscriptions() {
    if (this.props.isPushV2Enabled) {
      return [];
    }

    const subscriptionsDelegate: PushEventSubscriptionsDelegate = {
      ...makeEventSubscriptionsDelegate(this.pushCoordinator),
      onDraftFailed: (failedDraftDescription) => {
        this.setState({failedDraftDescription});
      },
      onReceiveImpersonationRequest: (impersonationRequest) => {
        this.setState({impersonationRequest});
      },
    };

    return makeDesktopPushEventSubscriptions(this.props, subscriptionsDelegate, this.props.dispatch);
  }

  // Every other push event that needs to be relayed to the old app
  // Current impl. is empty, but code can be added for the new app.
  private readonly relayedEvents = [
    'account_failed',
    'account_saved',
    'card_deleted',
    'card_group_deleted',
    'channel_credentials_failed',
    'channel_credentials_saved',
    'check_smtp_dns_result',
    'check_smtp_result_completed',
    'company_plan_changed',
    'company_status_changed',
    'contact_updated',
    'force_reload',
    'global_alert',
    'integration_test_completed',
    'notif_settings_updated',
    'notification_updated',
    'office365_import_saved',
    'rule_deleted',
    'rule_updated',
    'sequence_recipient_updated',
    'tag_counter_updated',
    'teammate_changed_update',
    'teammate_deleted',
    'teammate_invited_update_inboxes',
    'test_pusher',
    'validate_dns_result',
    'validate_smtp_address_result',
    'notification_rules_updated',
  ];

  /*
   * Refresh content logic.
   */

  private refreshContent(cancelToken: CancelToken) {
    // Refresh everything under this component.
    const {onRawBoot, refreshBoot, trySyncCellsListsAsync, syncStashWithServerAsync} = this.props;
    refreshBoot({onRawBoot, cancelToken});
    trySyncCellsListsAsync(SyncCellsListsReasonsEnum.RECONNECT, cancelToken);
    reconnectRuntimeFeatureFlagsAsync();
    syncStashWithServerAsync(cancelToken);

    inAnimationFrameOrHiddenCallbackAsync(
      () => {
        this.setState(({connectionKey}) => ({connectionKey: connectionKey + 1}));
      },
      {source: TaskSourcesEnum.PUSHER},
    );
  }

  /*
   * Rooms.
   */

  private getRooms() {
    const {
      cellsPanelsRooms,
      viewerPanelsRooms,
      composerPanelsRooms,
      analyticsPanelsRooms,
      shouldAlwaysSubscribeToUserFolderRoom,
    } = this.props;
    return chain(cellsPanelsRooms)
      .concat(viewerPanelsRooms)
      .concat(composerPanelsRooms)
      .concat(analyticsPanelsRooms)
      .concat(shouldAlwaysSubscribeToUserFolderRoom ? `teammatefolder-${this.props.user.id}` : '')
      .compact()
      .uniq()
      .value();
  }

  /*
   * Render.
   */

  render() {
    const {children} = this.props;
    const {connectionKey, isOffline} = this.state;
    const contextValue = this.pushCoordinator.buildContextValue(connectionKey, !isOffline);
    return (
      <PushContext.Provider value={contextValue}>
        {this.maybeRenderImpersonationRequestBanner()}
        {this.maybeRenderFailedDraftBanner()}
        {this.maybeRenderOfflineBanner()}
        {children}
      </PushContext.Provider>
    );
  }

  private maybeRenderImpersonationRequestBanner() {
    const {impersonationRequest} = this.state;
    if (!impersonationRequest || this.props.isPushV2Enabled) {
      return null;
    }

    // Optimistic update: immediately hide the banner but re-display it if there's an error.
    const onImpersonationResultAsync = async (granted: boolean) => {
      // Close the banner.
      this.setState({impersonationRequest: undefined});

      // Perform the request.
      try {
        await postAsync(`${this.props.user.url}/impersonate`, {granted});
      } catch (error) {
        this.setState((state) => (state.impersonationRequest ? null : {impersonationRequest}));
      }
    };

    return (
      <ImpersonationRequestBanner
        onAllowClick={() => onImpersonationResultAsync(true)}
        onDenyClick={() => onImpersonationResultAsync(false)}
      />
    );
  }

  private maybeRenderFailedDraftBanner() {
    const {failedDraftDescription} = this.state;
    if (!failedDraftDescription || this.props.isPushV2Enabled) {
      return null;
    }

    const onFailedDraftDismiss = () => {
      // Close the banner.
      this.setState({failedDraftDescription: undefined});
    };

    return (
      <FailedDraftBanner description={failedDraftDescription} onDismissClick={() => onFailedDraftDismiss()} />
    );
  }

  private maybeRenderOfflineBanner() {
    const {isOffline} = this.state;
    if (!isOffline || this.props.isPushV2Enabled) {
      return null;
    }

    return <OfflineBanner />;
  }
}

/*
 * Redux connection.
 */

const getShouldAlwaysSubscribeToUserFolderRoom = createSelector(
  [getUser],
  (user) => !isVisitorTeammate(user),
);

const mapStateToProps = makeMapStateToProps()(() => ({
  pusherConfiguration: getPushConfig,
  company: getCompany,
  user: getUser,
  viewerPanelsConversationIds: getViewerPanelsConversationIds,
  isNotificationCenterEnabled: getIsNotificationCenterEnabled,
  cellsPanelsRooms: getCellsPanelsRooms,
  viewerPanelsRooms: getViewerPanelsRooms,
  composerPanelsRooms: getComposerPanelsRooms,
  shouldAlwaysSubscribeToUserFolderRoom: getShouldAlwaysSubscribeToUserFolderRoom,
  analyticsPanelsRooms: getAnalyticsPanelsRooms,
  isPushV2Enabled: getHasPushV2,
  hasOfflineBannerAdjustment: getHasOfflineBannerAdjustment,
}));

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  refreshBoot: refreshBootAction,
  trySyncCellsListsAsync: trySyncUserCellsListsAsyncAction,
  syncStashWithServerAsync: syncStashWithServerAsyncAction,
  pushStatusDisconnected: pushStatusDisconnectedAction,
  pushConversationUpdated: pushConversationUpdatedAction,
}));

export const PushContainer = connect(mapStateToProps, mapDispatchToProps)(Push);
